import { Button } from "@acdc2/ui/components/button";
import { cn } from "@acdc2/ui/lib/utils";
import { mdiContentSave, mdiLoading, mdiTrashCan } from "@mdi/js";
import { Icon } from "@mdi/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@acdc2/ui/components/tooltip";
import { Badge } from "@acdc2/ui/components/badge";
import { Form } from "@acdc2/ui/components/access-form";

type Props = {
  title: string;
  unsaved?: boolean;
  onSubmit: () => Promise<void>;
  onDelete: () => Promise<void>;
  children?: React.ReactNode;
};

export function AttributePanel({
  title,
  unsaved,
  onSubmit,
  onDelete,
  children,
}: Props) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onSubmit();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Form className="flex flex-col border-b" onSubmit={handleSubmit}>
      <div className="flex flex-row justify-between items-center p-4 border-b border-dashed">
        <div className="flex flex-row items-center">
          <span>{title}</span>
          <div className="px-2">
            {unsaved && (
              <Badge variant="destructive">
                {t("AttributePanel.badges.unsaved")}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="submit"
                  variant="outline"
                  size="icon"
                  disabled={saving}
                >
                  <Icon
                    className={cn({ "animate-spin": saving })}
                    path={saving ? mdiLoading : mdiContentSave}
                    size="1.25rem"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("AttributePanel.tooltips.save")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  <Icon
                    className={cn({ "animate-spin": deleting })}
                    path={deleting ? mdiLoading : mdiTrashCan}
                    size="1.25rem"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t("AttributePanel.tooltips.delete")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </Form>
  );
}
